import React from "react";
import {
  Box,
  MenuItem,
  Select,
  FormControl,
  FormHelperText,
  InputAdornment,
  Input,
  TextField
} from "@mui/material";

import { questionnairesStyles } from "../questionnaire.styles";

import { COUNTRIES } from "../../../../config/app.constants";
import useForm from "./hooks/useForm";
import { InputFieldWrapper } from "../components/InputFieldWrapper.component";
import { ExtremeSportQuestionnaireBase } from "./components/ExtremeSportQuestionnaireBase.component";
import { ExtremeSportsClass } from "../../../../model/ExtremeSports.class";

/**
  @typedef {{
    depth: string;
    country: string;
    frequency: string;
    otherCountry: string;
  }} ScubaDivingValue
 */

/**
  @typedef {Omit<import("./hooks/useForm").FormProps<ScubaDivingValue, keyof ScubaDivingValue>, 'changeModifier'|'stateRules'>} ScubaDivingQuestionnaireFormProps
 */

export const stateRules = {
  depth: "required",
  country: "required",
  frequency: "required",
  otherCountry: (val, data) => {
    if (data.country !== "Others") return true;

    return Boolean(val);
  }
};

/**
  @param {ScubaDivingQuestionnaireFormProps} props 
 */
const ScubaDivingQuestionnaireForm = props => {
  const { textFieldRed } = questionnairesStyles();
  const { errorFields, handleUpdateField, state, handleKeyPress } = useForm({
    ...props,
    stateRules,
    changeModifier: (state, field, value) => {
      if (field === "country" && value !== "Others") {
        state.otherCountry = "";
      }
    }
  });

  return (
    <Box display="grid" gridTemplateColumns="repeat(3,1fr)" gap={2}>
      <InputFieldWrapper
        label={<Box fontWeight="bold">Country of Activity</Box>}
      >
        <FormControl variant="standard" sx={{ m: 0 }}>
          <Select
            value={state.country}
            onChange={event => {
              handleUpdateField("country", event.target.value);
            }}
            required
            error={errorFields.country.error}
          >
            {COUNTRIES.map(country => (
              <MenuItem key={country.id} value={country.title}>
                {country.title}
              </MenuItem>
            ))}
          </Select>
          {errorFields.country.error && (
            <FormHelperText style={{ color: "red" }}>
              This field is mandatory.
            </FormHelperText>
          )}
        </FormControl>
      </InputFieldWrapper>
      <InputFieldWrapper
        label={<Box fontWeight="bold">Frequency of Activity</Box>}
      >
        <Input
          variant="standard"
          className={textFieldRed}
          type="number"
          endAdornment={
            <InputAdornment position="end">times per year</InputAdornment>
          }
          required
          error={errorFields.frequency.error}
          onChange={event => {
            handleUpdateField("frequency", event.target.value);
          }}
          onKeyPress={handleKeyPress}
          value={state.frequency}
          inputProps={{ min: 1 }}
        />
        {errorFields.frequency.error && (
          <FormHelperText style={{ color: "red" }}>
            This field is mandatory.
          </FormHelperText>
        )}
      </InputFieldWrapper>
      <InputFieldWrapper label={<Box fontWeight="bold">Depth Dived</Box>}>
        <Input
          variant="standard"
          className={textFieldRed}
          type="number"
          endAdornment={<InputAdornment position="end">m</InputAdornment>}
          required
          error={errorFields.depth.error}
          onChange={event => {
            handleUpdateField("depth", event.target.value);
          }}
          onKeyPress={handleKeyPress}
          value={state.depth}
        />
        {errorFields.depth.error && (
          <FormHelperText style={{ color: "red" }}>
            This field is mandatory.
          </FormHelperText>
        )}
      </InputFieldWrapper>
      <InputFieldWrapper
        label={
          <Box
            fontWeight="bold"
            style={{
              color: state.country !== "Others" ? "grey" : "black"
            }}
          >
            Others, please specify
          </Box>
        }
      >
        <TextField
          variant="standard"
          className={textFieldRed}
          disabled={state.country !== "Others" ? true : false}
          error={errorFields.otherCountry.error}
          onChange={event => {
            handleUpdateField("otherCountry", event.target.value);
          }}
          value={state.otherCountry}
        />
        {errorFields.otherCountry.error ? (
          <FormHelperText style={{ color: "red" }}>
            This field is mandatory.
          </FormHelperText>
        ) : (
          ""
        )}
      </InputFieldWrapper>
    </Box>
  );
};

export const FORM_KEY = "scubaDiving";

/**
 * @param {import("./hooks/useHealthDeclaration").HealthDeclarationProps} props
 */
export const ScubaDivingQuestionnaire = props => (
  <ExtremeSportQuestionnaireBase
    {...props}
    onAddRecord={() => ({
      depth: "",
      country: "",
      frequency: "",
      otherCountry: ""
    })}
    stateRules={stateRules}
    extremeSportDataBuilder={v =>
      new ExtremeSportsClass(
        v.country,
        v.frequency,
        v.depth,
        null,
        v.otherCountry
      )
    }
    formItemMapper={({ item, onChange, onValidate }) => (
      <ScubaDivingQuestionnaireForm
        value={item}
        onChange={onChange}
        onValidate={onValidate}
        validateOnMount
      />
    )}
  />
);
